import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CompletionActions = ({ batchData, onNavigate }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');

  const { failedInvoices, warningInvoices } = batchData;
  const hasFailures = failedInvoices > 0;
  const hasWarnings = warningInvoices > 0;

  const handleExport = async (format) => {
    setIsExporting(true);
    setExportFormat(format);
    
    // Simulate export process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock download trigger
    const filename = `processing-report-${new Date()?.toISOString()?.split('T')?.[0]}.${format}`;
    console.log(`Downloading ${filename}`);
    
    setIsExporting(false);
  };

  const handleRetryFailed = () => {
    // Navigate to error correction page with failed invoices
    onNavigate('/invoice-error-correction');
  };

  const handleStartNewBatch = () => {
    // Navigate to processing queue to start new batch
    onNavigate('/invoice-processing-queue');
  };

  const handleContinueToManagement = () => {
    // Navigate to main invoice management system
    console.log('Navigating to Invoice Management System');
  };

  return (
    <div className="space-y-6">
      {/* Primary Actions */}
      <div className="bg-muted rounded-lg border border-border p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">Next Steps</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Button
            variant="default"
            size="lg"
            onClick={handleContinueToManagement}
            iconName="ArrowRight"
            iconPosition="right"
            className="w-full"
          >
            Continue to Invoice Management
          </Button>

          <Button
            variant="outline"
            size="lg"
            onClick={handleStartNewBatch}
            iconName="Plus"
            iconPosition="left"
            className="w-full"
          >
            Start New Batch
          </Button>
        </div>

        {hasFailures && (
          <div className="mt-4 p-4 bg-error/5 border border-error/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" />
              <span className="font-medium text-error">Action Required</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              {failedInvoices} invoice{failedInvoices > 1 ? 's' : ''} failed validation and require correction.
            </p>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleRetryFailed}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Retry Failed Items
            </Button>
          </div>
        )}

        {hasWarnings && !hasFailures && (
          <div className="mt-4 p-4 bg-warning/5 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
              <span className="font-medium text-warning">Review Recommended</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {warningInvoices} invoice{warningInvoices > 1 ? 's' : ''} completed with warnings that may need attention.
            </p>
          </div>
        )}
      </div>

      {/* Export Options */}
      <div className="bg-muted rounded-lg border border-border p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">Export Reports</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button
            variant="outline"
            onClick={() => handleExport('pdf')}
            loading={isExporting && exportFormat === 'pdf'}
            disabled={isExporting}
            iconName="FileText"
            iconPosition="left"
            className="w-full"
          >
            PDF Report
          </Button>

          <Button
            variant="outline"
            onClick={() => handleExport('csv')}
            loading={isExporting && exportFormat === 'csv'}
            disabled={isExporting}
            iconName="Download"
            iconPosition="left"
            className="w-full"
          >
            CSV Export
          </Button>

          <Button
            variant="outline"
            onClick={() => handleExport('xlsx')}
            loading={isExporting && exportFormat === 'xlsx'}
            disabled={isExporting}
            iconName="FileSpreadsheet"
            iconPosition="left"
            className="w-full"
          >
            Excel Export
          </Button>
        </div>

        <div className="mt-4 p-3 bg-muted rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} color="var(--color-text-secondary)" className="mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">Export includes:</p>
              <ul className="space-y-1 text-xs">
                <li>• Processing summary and metrics</li>
                <li>• Individual invoice results</li>
                <li>• Error details and recommendations</li>
                <li>• ZATCA compliance status</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="bg-muted rounded-lg border border-border p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">Quick Navigation</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Button
            variant="ghost"
            onClick={() => onNavigate('/invoice-processing-queue')}
            iconName="Clock"
            iconPosition="left"
            className="w-full justify-start"
          >
            Processing Queue
          </Button>

          <Button
            variant="ghost"
            onClick={() => onNavigate('/invoice-error-correction')}
            iconName="AlertTriangle"
            iconPosition="left"
            className="w-full justify-start"
          >
            Error Correction
          </Button>

          <Button
            variant="ghost"
            onClick={() => onNavigate('/batch-processing-overview')}
            iconName="Layers"
            iconPosition="left"
            className="w-full justify-start"
          >
            Batch Overview
          </Button>

          <Button
            variant="ghost"
            onClick={() => onNavigate('/processing-completion-summary')}
            iconName="CheckCircle"
            iconPosition="left"
            className="w-full justify-start"
          >
            Summary
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompletionActions;

